import { TranslateService } from '@ngx-translate/core';
import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router, Params } from '@angular/router';
import { LocalStorageService } from 'angular-2-local-storage';
import { Subscription } from 'rxjs';

import { BbSelectOption } from 'app/components/shared/bb-select/bb-select.component';
import { REDUX_STORE_PROJECT_FLOWCHART } from '../../../shared/redux/app.store';
import { UNDOABLE_TYPE } from '../../../shared/redux/enums/undoable.type.enum';

import { Publication } from '../../../interfaces/publication';
import { PublicationService } from '../../../services/publication.service';
import { DateRange } from '../../../interfaces/dateRange';
import { ProjectPosition, ProjectStatsConfiguration, Project, ProjectStep, ProjectFlowchart } from '../../../interfaces/project';

import { FlowchartService } from './../../../services/flowchart.service';
import { OverlayService } from '../../../services/overlay.service';
import { LocationService } from '../../../services/location.service';
import { ProjectService } from '../../../services/project.service';
import { ReduxService } from '../../../services/redux.service';

@Component({
	selector: 'project-stats',
	templateUrl: './project-stats.component.html',
	styleUrls: ['./project-stats.component.scss']
})

export class ProjectStatsComponent implements OnInit, OnDestroy {
	@ViewChild('canvas') canvas: any;

	selectedPosition: ProjectPosition;
	id: number;
	project: Project;
	projectId: string;
	activeTab: string;
	loadingProject = false;
	configuration: ProjectStatsConfiguration;
	statsCollectionStartDate: Date; // date we started collecting statistics for projects
	clipTitles: { [clipId: string]: string } = {};
	showChart = false;
	sankeyData: any = { nodes: [], links: [] };
	highlightedId: string;
	minDate: Date;

	projectSubscription: Subscription;
	loadingSubscription: Subscription;

	firstStep: ProjectStep;
	noFlowchart: boolean;
	flowchart: ProjectFlowchart;
	clipCount: number;

	projectTitle: string;
	projectDownloadUrl: string;
	projectStatsQueryParamsString: string;
	projectContextOptions: BbSelectOption[];

	historySubscription: Subscription;
	private stateHistory: Project[];
	private subscriptions = new Subscription();
	private lastDateRange: DateRange;
	private lastPerformanceDateRange: DateRange;
	private contextChanged = false;
	private tabsLoaded: string[] = [];

	constructor(private route: ActivatedRoute,
		private router: Router,
		private translate: TranslateService,
		public projectService: ProjectService,
		private localStorageService: LocalStorageService,
		private publicationService: PublicationService,
		private locationService: LocationService,
		private flowchartService: FlowchartService,
		protected reduxService: ReduxService,
		private overlayService: OverlayService) {

		this.loadConfig();
		this.reduxService.setup(REDUX_STORE_PROJECT_FLOWCHART, UNDOABLE_TYPE.PROJECT_FLOWCHART);
	}

	ngOnInit() {
		this.projectId = this.route.snapshot.paramMap.get('id');
		this.getLightProject(this.projectId);

		this.subscriptions.add(this.route.params.subscribe((params: Params) => {
			if (this.projectId !== params.id) {
				this.projectService.reset();
			}
			const previousTab = this.activeTab;
			this.activeTab = params.tab;
			this.projectId = params.id;

			if (previousTab !== this.activeTab) {
				setTimeout(() => {
					// timeout so the loader doesn't reset
					this.onTabChange(params.tab);
				}, 1);
			}
			if (this.tabsLoaded.indexOf(this.activeTab) === -1) {
				this.tabsLoaded.push(this.activeTab);
			}
		}));

		const loadId = 'ProjectStatsPerformanceComponent:ngOnInit:projectService.get';
		this.overlayService.increaseLoadCount(loadId);
		this.subscriptions.add(this.projectService.get(this.projectId).subscribe((project: Project) => {
			this.overlayService.decreaseLoadCount(loadId);
			this.setProject(project);
			this.firstStep = null;
			if (project) {
				this.clipCount = project.clipCount;
				if (project && project.clipCount === 1 && this.project.chapters) {
					for (const chapterIndex in this.project.chapters) {
						if (chapterIndex) {
							for (const step in this.project.chapters[chapterIndex].steps) {
								if (!this.firstStep) {
									this.firstStep = this.project.chapters[chapterIndex].steps[step];
								}
							}
						}
					}
					if (this.firstStep && !this.firstStep.entityId) {
						this.firstStep = null;
					}
					this.noFlowchart = !this.firstStep || !this.firstStep.entityId;
				} else {
					if (this.project.subtype === 'simple') {
						this.projectService.setStatsMode(true);
						const loadId2 = 'ProjectStatsPerformanceComponent:ngOnInit:getOldProjectAndGenerate';
						this.overlayService.increaseLoadCount(loadId2);
						this.subscriptions.add(this.projectService.getOldProjectAndGenerate(this.flowchartService.getGridRules(true))
							.subscribe((flowchart: ProjectFlowchart) => {
								this.flowchart = flowchart;
								this.overlayService.decreaseLoadCount(loadId2);
							}
							));
					} else {
						this.flowchart = this.project.flowchart;
					}
				}
				this.reduxService.dispatch({ history: 'reset', type: 'FROM_LOAD', loadData: { data: this.flowchart } });
			} else {
				this.noFlowchart = true;
			}

			if (this.project.publisheddate || this.project.createddate) {
				this.minDate = new Date(this.project.publisheddate || this.project.createddate);
				this.minDate.setHours(0, 0, 0, 0);
			}
			this.subscriptions.add(this.reduxService.historyObs.subscribe((historyData: any) => {
				if (!this.flowchart) {
					return;
				}
				this.stateHistory = [...historyData.past];
				if (historyData && historyData.present) {
					this.stateHistory.push(historyData.present);
					this.flowchart = historyData.present.data;
				}
			}));
		}));
	}

	ngOnDestroy() {
		this.subscriptions.unsubscribe();
		this.projectService.reset();
	}

	getLightProject(projectId: string) {
		this.subscriptions.add(this.projectService.getLightProject(projectId).subscribe((project: Project) => {
			this.setProject(project);
		}));
	}

	onContextChange() {
		const dateRange = {
			startDate: this.configuration.statsDateStart,
			endDate: this.configuration.statsDateEnd
		};

		if (this.activeTab === 'flow-analysis') {
			this.loadFlowAnalysisStats(dateRange, true);
		} else {
			this.projectService.setStatsDateRange(dateRange);
			this.contextChanged = true;
		}
	}

	setProject(project: Project) {
		this.projectService.setProject(project);
		this.projectDownloadUrl = this.projectService.projectDownloadUrl + project.id;
		this.project = project;
		this.projectContextOptions = project?.contexts?.map(c => (
			{
				label: c.label,
				value: c.code
			}
		)) || [];
		if (this.projectContextOptions.length > 0) {
			this.projectContextOptions.unshift({
				label: this.translate.instant('NONE'),
				value: '__none__',
				default: true
			});
		}
		if (this.project) {
			this.projectTitle = this.project.title || this.project.sourceid || this.project.name || '';
		}
	}

	loadConfig() {
		const configJson = this.localStorageService.get('project_stats_config');

		let config: ProjectStatsConfiguration;

		if (configJson) {
			config = JSON.parse(configJson as string);
		} else {
			config = {
				showEngagement: true,
				showKpis: true,
				showNavigation: false
			};
		}

		this.configuration = config;
	}

	downloadFormData() {
		this.projectService.downloadFormData();
	}

	downloadStatistics() {
		const loadId = 'ProjectStatsPerformanceComponent:downloadStatistics:publicationService.getData';
		this.overlayService.increaseLoadCount(loadId);
		this.subscriptions.add(this.publicationService.getData().subscribe((publication: Publication) => {
			this.overlayService.decreaseLoadCount(loadId);
			const context = this.projectService?.projectContext ? '&context=' + this.projectService.projectContext : '';
			const dates = 'fromDate=' + this.configuration.statsDateStart.toISOString() + '&toDate=' + this.configuration.statsDateEnd.toISOString();
			const url = this.locationService.server + '/sapi/project/' + this.project.id + '/sheet?' + dates + context;
			location.href = url;
		}));
	}

	dateToStartOfMonth(date: Date) {
		const startOfMonthDate = new Date(date);
		startOfMonthDate.setDate(1);
		startOfMonthDate.setHours(0, 0, 0, 0);
		return startOfMonthDate;

	}

	downloadPerformance() {
		const loadId = 'ProjectStatsPerformanceComponent:downloadPerformance:publicationService.getData';
		this.overlayService.increaseLoadCount(loadId);
		this.subscriptions.add(this.publicationService.getData().subscribe((publication: Publication) => {
			this.overlayService.decreaseLoadCount(loadId);
			const context = this.projectService?.projectContext ? '&context=' + this.projectService.projectContext : '';
			const dates = 'fromDate=' + this.dateToStartOfMonth(this.configuration.statsDateStart).toISOString() +
				'&toDate=' + this.configuration.statsDateEnd.toISOString();
			const url = this.locationService.server + '/sapi/project/' + this.project.id + '/performanceSheet?' + dates + context;
			location.href = url;
		}));
	}

	storeConfig() {
		// convert Dates to strings before saving json
		const config = { ... this.configuration } as any; // copy

		// we only store the dates for custom periods
		if (config.statsDatePreset === 'custom') {
			config.statsDateStart = this.configuration.statsDateStart.toString();
			config.statsDateEnd = this.configuration.statsDateEnd.toString();
		} else {
			delete config.statsDateStart;
			delete config.statsDateEnd;
		}

		this.localStorageService.set('project_stats_config', JSON.stringify(config));
	}

	configurationChanged(evt: any) {
		this.storeConfig();
	}

	loadFlowAnalysisStats(dateRange: DateRange, force = false) {
		if (!force
			&& (this.lastDateRange && dateRange && (dateRange.startDate === this.lastDateRange.startDate && dateRange.endDate === this.lastDateRange.endDate))
		) {
			return;
		}
		const loadId = 'ProjectStatsPerformanceComponent:loadProjectStats';
		this.overlayService.increaseLoadCount(loadId);
		this.projectService.setStatsDateRange(dateRange);
		this.lastDateRange = dateRange;
		this.projectService.getProjectStats(dateRange).subscribe(() => {
			this.overlayService.decreaseLoadCount(loadId);
		});
	}

	onTabChange(tab: string) {
		// if we navigate directly by url to flow-analysis (empty this.lastPerformanceDateRange), loadFlowAnalysisStats will be called by datesChanged
		if (tab === 'flow-analysis' && (this.lastPerformanceDateRange || this.contextChanged)) {
			this.loadFlowAnalysisStats(this.lastPerformanceDateRange, this.contextChanged);
			this.contextChanged = false;
		}
	}

	datesChanged(dateRange: DateRange) {
		if (this.configuration.statsDateStart === dateRange.startDate && this.configuration.statsDateEnd === dateRange.endDate) {
			return;
		};

		this.configuration.statsDateStart = dateRange.startDate;
		this.configuration.statsDateEnd = dateRange.endDate;
		this.storeConfig();

		if (this.activeTab === 'flow-analysis') {
			this.loadFlowAnalysisStats(dateRange);
		} else {
			this.projectService.setStatsDateRange(dateRange);
		}

		this.lastPerformanceDateRange = dateRange;
		setTimeout(() => {
			this.projectStatsQueryParamsString = this.projectService.getStatsQueryParamsString();
		}, 0);
	}

	tabLoadedBefore(tab: string) {
		return this.tabsLoaded.indexOf(tab) > -1;
	}

	exit(event: MouseEvent) {
		// If dirty, then confirmation...
		this.router.navigate(['/analytics/projects']);
	}
}
